import * as React from 'react'
import { Formik, Field, FieldProps } from 'formik'

import { LoginUser } from '@owl-nest/auth'
import { t, tc } from '@owl-nest/localize'
import * as service from '@owl-nest/services'
import * as env from '@owl-nest/config'
import * as plume from '@ulule/owl-kit-components/next'
import { ua } from '@owl-nest/utils'

import { useAuthFailureMessage } from './useAuthFailureMessage'
import { validateEmail, apiErrorGetter, validateRequired } from './validators'

import * as S from './styles'

type Translations = { cta: string }

type ExistingUserProps = {
  auth: service.user.UseAuthExisting
  compactMode?: boolean
  showHint?: boolean
  translations?: Translations
}

export function ExistingUser({
  auth,
  compactMode = false,
  showHint = true,
  translations,
}: ExistingUserProps): React.ReactElement<ExistingUserProps> {
  if (auth.service === 'ulule') {
    return (
      <UluleExistingUser auth={auth} compactMode={compactMode} showHint={showHint} translations={translations} />
    )
  } else {
    return <ThirdPartyAuthKnownUserForm auth={auth} showHint={showHint} />
  }
}

function ThirdPartyAuthKnownUserForm({
  auth,
  showHint = true,
}: ExistingUserProps): React.ReactElement<ExistingUserProps> {
  const isWebView = ua.isWebview(window.navigator.userAgent)

  return (
    <S.ThirdPartyAuthExistingUserForm noValidate onSubmit={(evt: React.FormEvent) => evt.preventDefault()}>
      {showHint && (
        <plume.styles.copy.M.light>
          {t('To continue, fill in your email or choose to log in with Facebook or Google.')}
        </plume.styles.copy.M.light>
      )}
      <plume.TextField label={t('E-mail address')} type="username" disabled value={auth.identifier.value}>
        <plume.styles.copy.S.regular>
          <plume.Link kind="secondary" onClick={() => auth.forget()}>
            {t('Change address')}
          </plume.Link>
        </plume.styles.copy.S.regular>
      </plume.TextField>
      <plume.SocialButton
        platform="facebook"
        style="round"
        type="button"
        onClick={() => {
          window.location.href = `${env.FACEBOOK_LOGIN_URL}?next=${window.location.href}`
        }}
      >
        {t('Log in with Facebook')}
      </plume.SocialButton>
      <plume.Callout type="info">
        {t('Connect via Facebook to confirm your identity, this Ulule account is associated with Facebook')}
      </plume.Callout>

      <plume.SocialButton
        platform="google"
        style="round"
        type="button"
        disabled={isWebView}
        onClick={() => {
          window.location.href = `${env.GOOGLE_LOGIN_URL}?next=${window.location.href}`
        }}
      >
        {t('Log in with Google')}
      </plume.SocialButton>
      {!isWebView && (
        <plume.Callout type="info">
          {t('Connect via Google to confirm your identity, this Ulule account is associated with Google')}
        </plume.Callout>
      )}
      {isWebView && (
        <S.StyledBanner type="alert">
          <plume.styles.copy.S.regular>
            {t("To use the Google connection, open this page in your phone's browser.")}
          </plume.styles.copy.S.regular>
        </S.StyledBanner>
      )}

      <S.ResetButton $adjustForHint={showHint} onClick={() => auth.forget()} type="button">
        {t('Reinitialize')}
      </S.ResetButton>
    </S.ThirdPartyAuthExistingUserForm>
  )
}

type Form = LoginUser

function UluleExistingUser({
  auth,
  compactMode,
  showHint,
  translations,
}: ExistingUserProps): React.ReactElement<ExistingUserProps> {
  useAuthFailureMessage(auth)

  const [loading, setLoading] = React.useState(false)
  const getApiErrors = apiErrorGetter<Form>(auth.failure)

  const originUrl = document !== undefined ? document.location.href : undefined

  return (
    <Formik<Form>
      initialValues={{ username: auth.identifier.value, password: '', 'stay-connected': false }}
      onSubmit={(user) => {
        setLoading(true)
        auth.login(user)
      }}
    >
      {({ handleSubmit, values, validateField }) => {
        const apiError = getApiErrors({
          password: {
            OAuth2ServerError: tc(
              'Please enter a correct username and password. Note that both fields are case-sensitive. Do you want to [link: create an account] ?',
              { link: <plume.Link kind="primary" onClick={() => auth.toNew(values.username)} /> },
            ),
          },
        })
        const hasNoApiError =
          apiError('password') === undefined &&
          apiError('stay-connected') === undefined &&
          apiError('username') === undefined

        return (
          <S.ExistingUserForm noValidate onSubmit={handleSubmit}>
            {showHint && (
              <plume.styles.copy.M.light>{t('To continue, enter your password.')}</plume.styles.copy.M.light>
            )}
            <S.ExistingUserFormFields>
              <Field
                name="username"
                validate={auth.identifier.type === 'email' ? (value: any) => validateEmail(value) : validateRequired}
              >
                {({ field, meta }: FieldProps<string>) => {
                  React.useEffect(() => {
                    validateField(field.name)
                  }, [field.value])
                  return (
                    <plume.TextField
                      label={t('E-mail address')}
                      placeholder={t('E-mail address')}
                      type="username"
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={apiError('password') || meta.error}
                    />
                  )
                }}
              </Field>
              <Field name="password" validate={validateRequired}>
                {({ field, meta }: FieldProps<string>) => {
                  React.useEffect(() => {
                    validateField(field.name)
                  }, [field.value])
                  return (
                    <plume.TextField
                      autoFocus
                      placeholder={t('Password')}
                      label={t('Password')}
                      type="password"
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      error={meta.touched ? meta.error : undefined}
                    />
                  )
                }}
              </Field>
              <plume.RoundButton fitToParent loading={loading && hasNoApiError} type="submit">
                {t(translations?.cta ?? 'Continue')}
              </plume.RoundButton>
            </S.ExistingUserFormFields>

            <S.ExistingUserFormBlock>
              <Field name="stay-connected">
                {({ field }: FieldProps<boolean>) => {
                  return (
                    <plume.CheckboxField
                      label={t('Keep me logged in')}
                      name={field.name}
                      checked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )
                }}
              </Field>

              <plume.Link href={`${env.PASSWORD_RESET_URL}?origin_url=${originUrl}`}>
                {t('Forgot your password?')}
              </plume.Link>
            </S.ExistingUserFormBlock>

            <plume.RoundButton fitToParent type="submit">
              {t(translations?.cta ?? 'Continue')}
            </plume.RoundButton>
            <S.ResetButton
              $adjustForHint={showHint}
              $adjustForCompactMode={compactMode}
              onClick={() => auth.forget()}
              type="button"
            >
              {t('Reinitialize')}
            </S.ResetButton>
          </S.ExistingUserForm>
        )
      }}
    </Formik>
  )
}
